import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, COMPANY } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent. We'll reply within 1 working hour.");
    } catch (err) {
      toast.error("Failed to send — please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="border-b border-ink-600">
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 border-x border-ink-600">
        <section className="col-span-12 md:col-span-5 p-8 md:p-12 border-b md:border-b-0 md:border-r border-ink-600">
          <div className="font-mono-tech text-[10px] text-copper mb-4">/ CONTACT</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">
            Find the workshop.
          </h1>
          <p className="text-steel-300 leading-relaxed mb-10">
            Give us a ring, drop us a message, or send us your VRN. Trade
            accounts welcome. We answer enquiries quickly during opening hours.
          </p>

          <div className="space-y-6">
            {[
              [MapPin, "Address", COMPANY.address, null],
              [Phone, "Telephone / WhatsApp", COMPANY.phone, `tel:${COMPANY.phone.replace(/\s+/g, "")}`],
              [Mail, "Email", COMPANY.email, `mailto:${COMPANY.email}`],
              [Clock, "Hours", COMPANY.hours, null],
            ].map(([Icon, k, v, href]) => {
              const inner = (
                <>
                  <span className="grid place-items-center w-11 h-11 border border-ink-600 text-copper shrink-0">
                    <Icon size={18} strokeWidth={1.8} />
                  </span>
                  <div>
                    <div className="font-mono-tech text-[10px] text-steel-500 mb-1">{k.toUpperCase()}</div>
                    <div className="text-sm break-words">{v}</div>
                  </div>
                </>
              );
              return href ? (
                <a
                  key={k}
                  href={href}
                  data-testid={`contact-info-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex gap-4 border-b border-ink-600 pb-5 hover:text-copper transition-colors"
                >
                  {inner}
                </a>
              ) : (
                <div key={k} className="flex gap-4 border-b border-ink-600 pb-5">
                  {inner}
                </div>
              );
            })}
          </div>
        </section>

        <section className="col-span-12 md:col-span-7 p-8 md:p-12 bg-ink-800">
          {done ? (
            <div data-testid="contact-success" className="h-full flex flex-col justify-center">
              <CheckCircle2 size={48} className="text-copper mb-6" strokeWidth={1.5} />
              <h2 className="font-display text-4xl md:text-5xl mb-4">Message received.</h2>
              <p className="text-steel-300 mb-8 max-w-lg">
                Thanks for getting in touch. A technician will reply to {form.email || "your email"} shortly.
              </p>
              <button onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", message: "" }); }} className="btn-ghost self-start">
                Send another message <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-2 gap-4">
              <Field label="Name *" testid="contact-name" value={form.name} onChange={set("name")} required />
              <Field label="Email *" type="email" testid="contact-email" value={form.email} onChange={set("email")} required />
              <div className="col-span-2">
                <Field label="Subject *" testid="contact-subject" value={form.subject} onChange={set("subject")} required />
              </div>
              <div className="col-span-2">
                <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">MESSAGE *</label>
                <textarea
                  data-testid="contact-message"
                  required
                  minLength={4}
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className="w-full bg-ink-900 border border-ink-600 px-3 py-3 outline-none focus:border-copper focus:ring-1 focus:ring-copper text-sm placeholder:text-steel-500 resize-none"
                />
              </div>
              <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-copper disabled:opacity-60">
                  {submitting ? "Sending…" : "Send Message"} <ArrowRight size={14} />
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] text-steel-400 block mb-2">{label}</label>
      <input
        {...rest}
        data-testid={testid}
        className="w-full bg-ink-900 border border-ink-600 px-3 py-3 outline-none focus:border-copper focus:ring-1 focus:ring-copper text-sm placeholder:text-steel-500"
      />
    </div>
  );
}
